import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from 'react-toastify';
import { 
  FaShoppingCart, 
  FaMapMarkerAlt, 
  FaFileAlt, 
  FaCreditCard, 
  FaArrowLeft, 
  FaCheck, 
  FaLock, 
  FaTimes,
  FaMobileAlt,
  FaUniversity,
  FaWallet,
  FaShieldAlt,
  FaCheckCircle,
  FaSpinner,
  FaTruck,
  FaClock,
  FaFlask,
  FaGift
} from "react-icons/fa";
import { 
  SiPaytm,
  SiRazorpay
} from "react-icons/si";
import { useCart } from "../../contexts/CartContext";
import api from "../../utils/api";
import DealReturnPolicyDisclosure, {
  allDealReturnPoliciesAccepted
} from '../deals/DealReturnPolicyDisclosure';
import { orderService } from "../../services/apiService";
import WorkflowWrapper from "../workflow/WorkflowWrapper";
import PaymentOptionCard from "./PaymentOptionCard";
import { useNotifications } from "../../contexts/NotificationContext";
import { formatPrice } from "../../utils/currency";
import { 
  getShippingOptions, 
  isDomesticShipping, 
  getShippingCharge,
  getDefaultShippingMethod 
} from "../../utils/shipping";

const Payment = () => {
  const navigate = useNavigate();
  const {
    cartItems: cartContextItems,
    getTotalPrice: getCartTotalPrice,
    getSubtotal: getCartSubtotal,
    getTotalItems: getCartTotalItems,
    appliedCoupon,
    applyCoupon,
    getCouponDiscount,
    clearCart
  } = useCart();
  const { notifyOrderStatus, notifyPayment } = useNotifications();

  // Buy-now mode: a single product purchased directly, bypassing the cart
  const [buyNowItem, setBuyNowItem] = useState(null);
  const isBuyNow = !!buyNowItem;

  // Effective item list — either the single buy-now product or the full cart
  const cartItems = isBuyNow ? [buyNowItem] : cartContextItems;
  const isUsedPartExpressBuy =
    isBuyNow &&
    (buyNowItem?.usedPartExpressBuy || buyNowItem?.usedPartMeta?.flowType === 'express_buy');

  // Derived totals that work for both modes
  const getTotalItems = () => cartItems.reduce((t, i) => t + (i.quantity || 1), 0);
  const getSubtotal = () => cartItems.reduce((t, i) => t + ((i.discountPrice ?? i.price ?? 0) * (i.quantity || 1)), 0);
  const getTotalPrice = () => getSubtotal();
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [selectedShippingMethods, setSelectedShippingMethods] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [luckyDrawTickets, setLuckyDrawTickets] = useState([]);
  const [createdOrder, setCreatedOrder] = useState(null);
  const createdOrderRef = useRef(null);
  const confirmationNavTimeoutRef = useRef(null);
  const [serviceSchedule, setServiceSchedule] = useState({
    date: '',
    timeSlot: '',
    pincode: '',
    vehicleNumber: ''
  });

  // Payment form data
  const [paymentData, setPaymentData] = useState({
    upiId: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    bankName: '',
    paytmPhone: '',
  });

  useEffect(() => {
    const savedAddress = localStorage.getItem('shippingAddress');
    if (savedAddress) {
      setShippingAddress(JSON.parse(savedAddress));
    } else {
      navigate('/checkout/address');
    }

    // Detect buy-now mode
    try {
      const raw = localStorage.getItem('buyNowItem');
      if (raw) setBuyNowItem(JSON.parse(raw));
    } catch (_) {}

    // Load shipping methods from localStorage
    const savedShippingMethods = localStorage.getItem('selectedShippingMethods');
    if (savedShippingMethods) {
      setSelectedShippingMethods(JSON.parse(savedShippingMethods));
    }
    // Load lucky draw tickets from Review step
    try {
      const saved = localStorage.getItem('luckyDrawTickets');
      if (saved) {
        const tickets = JSON.parse(saved);
        if (Array.isArray(tickets)) setLuckyDrawTickets(tickets);
      }
    } catch (_) {}
    try {
      const savedServiceSchedule = localStorage.getItem('serviceSchedule');
      if (savedServiceSchedule) {
        const parsed = JSON.parse(savedServiceSchedule);
        setServiceSchedule((prev) => ({ ...prev, ...(parsed || {}) }));
      }
    } catch (_) {}
  }, [navigate]);

  useEffect(() => {
    try {
      localStorage.setItem('serviceSchedule', JSON.stringify(serviceSchedule));
    } catch (_) {}
  }, [serviceSchedule]);

  useEffect(() => {
    return () => {
      if (confirmationNavTimeoutRef.current) {
        clearTimeout(confirmationNavTimeoutRef.current);
      }
    };
  }, []);

  const goToOrderConfirmation = useCallback(() => {
    const order = createdOrderRef.current || createdOrder;
    if (!order) {
      toast.error('Order details are not available. Please check My Orders.');
      navigate('/myorder');
      return;
    }

    if (confirmationNavTimeoutRef.current) {
      clearTimeout(confirmationNavTimeoutRef.current);
      confirmationNavTimeoutRef.current = null;
    }

    try {
      sessionStorage.setItem('lastCheckoutOrder', JSON.stringify(order));
    } catch (_) {}

    setShowSuccessModal(false);
    navigate('/checkout/confirmation', {
      state: { order, paymentSuccess: true },
      replace: true,
    });
  }, [createdOrder, navigate]);

  // Group items by seller to create packages
  const packages = useMemo(() => {
    const packageMap = {};
    cartItems.forEach((item) => {
      const seller = item.seller || "Default Seller";
      if (!packageMap[seller]) {
        packageMap[seller] = [];
      }
      packageMap[seller].push(item);
    });
    return Object.entries(packageMap).map(([seller, items], index) => ({
      packageNumber: index + 1,
      seller,
      items,
    }));
  }, [cartItems]);

  // Get shipping options based on address (domestic/international)
  const shippingOptions = useMemo(() => {
    if (!shippingAddress) return [];
    return getShippingOptions(shippingAddress, 0);
  }, [shippingAddress]);

  // Check if shipping is domestic or international
  const isDomestic = useMemo(() => {
    return isDomesticShipping(shippingAddress);
  }, [shippingAddress]);

  // Get delivery charge based on shipping method
  const getDeliveryChargeForMethod = useCallback((packageTotal, shippingMethodId) => {
    if (!shippingAddress) return 0;
    const defaultMethod = getDefaultShippingMethod(shippingAddress);
    const methodId = shippingMethodId || defaultMethod;
    return getShippingCharge(methodId, packageTotal, shippingAddress);
  }, [shippingAddress]);

  // Calculate delivery charge per package
  const getDeliveryCharge = useCallback((packageTotal, packageNumber) => {
    const shippingMethodId = selectedShippingMethods[packageNumber] || 'standard';
    return getDeliveryChargeForMethod(packageTotal, shippingMethodId);
  }, [selectedShippingMethods, getDeliveryChargeForMethod]);

  // Calculate totals
  const totalDeliveryCharge = useMemo(() => {
    return packages.reduce((total, pkg) => {
      const packageTotal = pkg.items.reduce((sum, item) => {
        return sum + ((item.discountPrice || item.price) * item.quantity);
      }, 0);
      return total + getDeliveryCharge(packageTotal, pkg.packageNumber);
    }, 0);
  }, [packages, getDeliveryCharge]);

  const platformFee = 120;
  const luckyDrawTotal = useMemo(() => {
    return luckyDrawTickets.reduce((sum, t) => sum + (t.ticketPrice || 0) * (t.quantity || 0), 0);
  }, [luckyDrawTickets]);
  const couponDiscount = useMemo(() => getCouponDiscount(), [getCouponDiscount, appliedCoupon]);
  const grandTotal = useMemo(() => {
    return Math.max(0, getTotalPrice() + totalDeliveryCharge + platformFee + luckyDrawTotal - couponDiscount);
  }, [getTotalPrice, totalDeliveryCharge, luckyDrawTotal, couponDiscount]);
  const hasGarageServiceDealFromCart = useMemo(() => {
    return cartItems.some((item) => String(item?.dealCategory || item?.category || '').toLowerCase() === 'garage_service');
  }, [cartItems]);
  const [hasGarageServiceDeal, setHasGarageServiceDeal] = useState(false);
  const [garageServiceNoReturnAccepted, setGarageServiceNoReturnAccepted] = useState(false);
  const [garageBookingHint, setGarageBookingHint] = useState('');
  const primaryGarageDealId = useMemo(() => {
    const hit = cartItems.find(
      (item) =>
        item?.dealId &&
        String(item?.dealCategory || item?.category || '').toLowerCase() === 'garage_service'
    );
    if (hit?.dealId) return hit.dealId;
    const anyDeal = cartItems.find((item) => item?.dealId);
    return anyDeal?.dealId || null;
  }, [cartItems]);
  const hasAnyDealItem = useMemo(() => {
    return cartItems.some((item) => !!item?.dealId);
  }, [cartItems]);
  const hasClearanceDealItem = useMemo(() => {
    return cartItems.some((item) => String(item?.dealCategory || item?.category || '').toLowerCase() === 'clearance');
  }, [cartItems]);
  const [clearanceNoReturnAccepted, setClearanceNoReturnAccepted] = useState(false);
  const cartDealIds = useMemo(
    () => [...new Set(cartItems.map((item) => item?.dealId).filter(Boolean))],
    [cartItems]
  );
  const [dealReturnPolicyAccepted, setDealReturnPolicyAccepted] = useState(false);
  const [flashCascadeItems, setFlashCascadeItems] = useState([]);
  const [flashCascadeAccepted, setFlashCascadeAccepted] = useState(false);
  const [showFlashCascadeModal, setShowFlashCascadeModal] = useState(false);

  useEffect(() => {
    setDealReturnPolicyAccepted(allDealReturnPoliciesAccepted(cartDealIds, 'checkout'));
  }, [cartDealIds]);

  useEffect(() => {
    const flashLines = cartItems.filter(
      (item) => item?.dealId && String(item?.dealCategory || item?.category || '').toLowerCase() === 'flash'
    );
    if (!flashLines.length || flashCascadeAccepted) {
      setFlashCascadeItems([]);
      setShowFlashCascadeModal(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/deals/flash/cascade-check', {
          items: flashLines.map((item) => ({
            dealId: item.dealId,
            productId: item.id || item.productId,
            quantity: item.quantity || 1
          }))
        });
        if (cancelled) return;
        const needs = res.data?.data?.items || [];
        setFlashCascadeItems(needs);
        setShowFlashCascadeModal(needs.length > 0);
      } catch {
        if (!cancelled) {
          setFlashCascadeItems([]);
          setShowFlashCascadeModal(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartItems, flashCascadeAccepted]);

  useEffect(() => {
    if (hasGarageServiceDealFromCart) {
      setHasGarageServiceDeal(true);
      return;
    }
    const dealIds = [...new Set(cartItems.map((item) => item?.dealId).filter(Boolean))];
    if (!dealIds.length) {
      setHasGarageServiceDeal(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/deals');
        const deals = res?.data?.data?.deals || res?.data?.deals || [];
        const found = deals.some(
          (d) =>
            dealIds.includes(String(d._id || d.id)) &&
            String(d.category || '').toLowerCase() === 'garage_service'
        );
        if (!cancelled) setHasGarageServiceDeal(found);
      } catch {
        if (!cancelled) setHasGarageServiceDeal(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartItems, hasGarageServiceDealFromCart]);

  useEffect(() => {
    if (!hasGarageServiceDeal || !primaryGarageDealId || !serviceSchedule?.date) {
      setGarageBookingHint('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const booking = await orderService.getGarageBookingStatus(primaryGarageDealId, {
          date: serviceSchedule.date,
          timeSlot: serviceSchedule.timeSlot || undefined
        });
        if (cancelled) return;
        if (booking?.fullyBooked) {
          setGarageBookingHint('This date is fully booked. Please choose another date.');
        } else if (booking?.slotBooked) {
          setGarageBookingHint('This time slot is already booked. Please choose another slot.');
        } else {
          setGarageBookingHint('');
        }
      } catch {
        if (!cancelled) setGarageBookingHint('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasGarageServiceDeal, primaryGarageDealId, serviceSchedule?.date, serviceSchedule?.timeSlot]);

  const mapOrderErrorToMessage = useCallback((rawMessage) => {
    const message = String(rawMessage || '');
    if (message.includes('COMBO_E001')) return 'This combo deal is currently inactive.';
    if (message.includes('COMBO_E002')) return 'This combo is not configured correctly. Please try another deal.';
    if (message.includes('COMBO_E003')) return 'Combo requirements are not met. Please add all required combo parts.';
    if (message.includes('SELLER_OWN_E001')) return 'Only the seller can create this seller-own deal.';
    if (message.includes('SELLER_OWN_E002')) return 'This seller-own deal is currently inactive.';
    if (message.includes('SELLER_OWN_E003')) return 'Seller-own deal mapping is missing. Please try again later.';
    if (message.includes('SELLER_OWN_E004')) return 'Seller-own deal can be used only with its mapped product.';
    if (message.includes('GARAGE_SERVICE_E001')) return 'Only the garage owner can create this garage service deal.';
    if (message.includes('GARAGE_SERVICE_E002')) return 'This garage service deal is currently inactive.';
    if (message.includes('GARAGE_SERVICE_E003')) return 'Your role is not eligible for this garage service deal.';
    if (message.includes('GARAGE_SERVICE_E004')) return 'Garage service deal can be used only with its mapped service product.';
    if (message.includes('GARAGE_SERVICE_E005')) return 'Please provide service date, time slot, and pincode.';
    if (message.includes('GARAGE_SERVICE_E006')) return 'Service is not available in the selected pincode.';
    if (message.includes('GARAGE_SERVICE_E007')) return 'Vehicle number is required for this garage service.';
    if (message.includes('GARAGE_SERVICE_E008')) return 'Selected service date is fully booked. Please choose another date.';
    if (message.includes('GARAGE_SERVICE_E009')) return 'This time slot is already booked. Please choose another slot.';
    if (message.includes('GARAGE_SERVICE_E010')) return 'You must accept the garage service no-return policy to continue.';
    if (message.includes('GARAGE_SERVICE_E011')) return 'Garage service bookings cannot be returned.';
    if (message.includes('GARAGE_SERVICE_E012')) return 'Garage service bookings must be cancelled as a whole booking.';
    if (message.includes('RETURN_POLICY_E001')) return 'This order includes items that cannot be returned (final sale).';
    if (message.includes('RETURN_POLICY_E002')) return 'Combo returns require all bundle items to be returned together.';
    if (message.includes('RETURN_POLICY_E003')) return 'Please accept the deal return policy at checkout before placing your order.';
    if (message.includes('CLEARANCE_E001')) return 'Clearance deal quantity exceeds the per-buyer limit.';
    if (message.includes('CLEARANCE_E002')) return 'This clearance deal is not active.';
    if (message.includes('CLEARANCE_E005')) return 'You must accept the no-return policy for clearance items.';
    if (message.includes('CLEARANCE_E006')) return 'Clearance orders cannot be returned (final sale).';
    return message || 'Failed to create order';
  }, []);

  const paymentOptions = useMemo(() => [
    {
      id: 'paytm',
      name: 'Paytm',
      icon: SiPaytm,
      color: '#00BAF2',
      description: 'Pay using Paytm Wallet or UPI',
      popular: true,
    },
    {
      id: 'razorpay',
      name: 'Razorpay',
      icon: SiRazorpay,
      color: '#0C2451',
      description: 'Secure payment gateway',
      popular: true,
    },
    {
      id: 'upi',
      name: 'UPI',
      icon: FaMobileAlt,
      color: '#5F259F',
      description: 'Google Pay, PhonePe, BHIM & more',
      popular: true,
    },
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: FaCreditCard,
      color: '#1A1F71',
      description: 'Visa, Mastercard, RuPay & more',
      popular: false,
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: FaUniversity,
      color: '#0066CC',
      description: 'All major banks supported',
      popular: false,
    },
    {
      id: 'cod',
      name: 'Cash on Delivery',
      icon: FaWallet,
      color: '#FF6B35',
      description: 'Pay when you receive the order',
      popular: false,
    },
    {
      id: 'dummy',
      name: 'Dummy Payment',
      icon: FaFlask,
      color: '#6B7280',
      description: 'Test payment – no real charge (for demo only)',
      popular: false,
    },
  ], []);

  const isUsedPartCheckAvailability =
    isBuyNow &&
    (buyNowItem?.usedPartCheckAvailability ||
      buyNowItem?.usedPartMeta?.flowType === 'check_availability' ||
      buyNowItem?.bodyPartMeta?.flowType === 'check_availability' ||
      buyNowItem?.bodyPartCheckout);

  const visiblePaymentOptions = useMemo(() => {
    return paymentOptions.filter((option) => {
      if (hasGarageServiceDeal && option.id === 'cod') return false;
      if (isUsedPartExpressBuy && option.id === 'cod') return false;
      if (isUsedPartCheckAvailability && option.id === 'cod') return false;
      return true;
    });
  }, [paymentOptions, hasGarageServiceDeal, isUsedPartExpressBuy, isUsedPartCheckAvailability]);

  const paymentMethodsNeedingForm = useMemo(
    () => new Set(['upi', 'card', 'netbanking', 'paytm']),
    []
  );

  const handlePaymentOptionClick = useCallback(
    (optionId) => {
      setSelectedPayment(optionId);
      if (paymentMethodsNeedingForm.has(optionId)) {
        setShowPaymentModal(true);
      } else {
        setShowPaymentModal(false);
      }
    },
    [paymentMethodsNeedingForm]
  );

  const handleCreateOrder = async () => {
    try {
      setPaymentProcessing(true);
      const isDummyPayment = selectedPayment === 'dummy';

      if (hasGarageServiceDeal && selectedPayment === 'cod') {
        toast.error('Garage service requires advance payment. Cash on delivery is not available for service bookings.');
        setPaymentProcessing(false);
        return;
      }

      if (hasGarageServiceDeal && garageBookingHint) {
        toast.error(garageBookingHint);
        setPaymentProcessing(false);
        return;
      }

      // Re-validate applied coupon against selected payment method before order create.
      if (appliedCoupon?.code) {
        try {
          await applyCoupon(appliedCoupon.code, selectedPayment);
        } catch (couponError) {
          const failures = Array.isArray(couponError?.failures) ? couponError.failures : [];
          if (failures.length > 0) {
            failures.forEach((f) => toast.error(f.message || 'Coupon cannot be applied', { autoClose: 5000 }));
          } else {
            toast.error(couponError?.message || 'Coupon is not valid for this payment method');
          }
          setPaymentProcessing(false);
          return;
        }
      }

      // Merge saved company/GST billing info (from Company GST page) into shipping address for invoice
      let finalShippingAddress = { ...shippingAddress };
      // Ensure required fields for backend (Order validation: shippingAddress.name and .mobile required)
      const nameVal = (finalShippingAddress.name != null && String(finalShippingAddress.name).trim()) || 'Customer';
      const mobileVal = (finalShippingAddress.mobile != null && String(finalShippingAddress.mobile).trim()) || 'N/A';
      finalShippingAddress = { ...finalShippingAddress, name: nameVal, mobile: mobileVal };
      try {
        const billingRaw = localStorage.getItem('companyBillingInfo');
        if (billingRaw) {
          const billing = JSON.parse(billingRaw);
          if (billing.gstin) finalShippingAddress.gstin = billing.gstin;
          if (billing.companyName) finalShippingAddress.companyName = billing.companyName;
        }
      } catch (_) {}

      if (hasGarageServiceDeal) {
        if (!String(serviceSchedule.date || '').trim() || !String(serviceSchedule.timeSlot || '').trim() || !String(serviceSchedule.pincode || '').trim()) {
          toast.error('Please select service date, time slot, and pincode for garage service.');
          setPaymentProcessing(false);
          return;
        }
      }

      if (cartDealIds.length > 0 && !dealReturnPolicyAccepted) {
        toast.error('Please review and accept the deal return policy at checkout before placing your order.');
        setPaymentProcessing(false);
        return;
      }

      if (hasClearanceDealItem && !clearanceNoReturnAccepted && !dealReturnPolicyAccepted) {
        toast.error('Please accept the clearance no-return policy (final sale) to continue.');
        setPaymentProcessing(false);
        return;
      }

      if (hasGarageServiceDeal && !garageServiceNoReturnAccepted) {
        toast.error('Please accept the garage service booking terms (no returns) to continue.');
        setPaymentProcessing(false);
        return;
      }

      if (hasGarageServiceDeal && selectedPayment === 'cod') {
        toast.error('Garage service requires advance payment online. Cash on delivery is not available for service bookings.');
        setPaymentProcessing(false);
        return;
      }

      if (isUsedPartExpressBuy && selectedPayment === 'cod') {
        toast.error('Express Buy requires immediate online payment. Cash on delivery is not available.');
        setPaymentProcessing(false);
        return;
      }

      if (isUsedPartCheckAvailability && selectedPayment === 'cod') {
        toast.error('Check Availability requires online payment after seller photo approval.');
        setPaymentProcessing(false);
        return;
      }

      if (hasGarageServiceDeal && garageBookingHint) {
        toast.error(garageBookingHint);
        setPaymentProcessing(false);
        return;
      }

      if (flashCascadeItems.length > 0 && !flashCascadeAccepted) {
        setShowFlashCascadeModal(true);
        toast.info('Primary seller is out of stock. Please confirm backup seller fulfillment.');
        setPaymentProcessing(false);
        return;
      }

      const orderData = {
        items: cartItems.map(item => ({
          productId: item.id || item.productId,
          dealId: item.dealId || null,
          name: item.name,
          brand: item.brand,
          imageUrl: item.imageUrl,
          price: item.price,
          discountPrice: item.discountPrice,
          quantity: item.quantity,
          seller: item.seller,
          partNumber: item.partNumber,
          vendorId: item.vendorId
        })),
        shippingAddress: finalShippingAddress,
        shippingMethods: selectedShippingMethods,
        paymentMethod: selectedPayment,
        paymentStatus: isDummyPayment ? 'Paid' : 'Pending',
        status: isDummyPayment ? 'Confirmed' : 'Pending Payment',
        subtotal: getSubtotal(),
        deliveryCharge: totalDeliveryCharge,
        platformFee: platformFee,
        total: grandTotal,
        totalItems: getTotalItems(),
        coupon: appliedCoupon ? {
          dealId: appliedCoupon.dealId,
          code: appliedCoupon.code,
          discountAmount: Number(appliedCoupon.discountAmount) || 0
        } : undefined,
        paymentDetails: {
          method: selectedPayment,
          timestamp: new Date().toISOString(),
          ...(isDummyPayment && { dummy: true, note: 'Test payment - no charge' })
        },
        luckyDrawTickets: Array.isArray(luckyDrawTickets) && luckyDrawTickets.length > 0
          ? luckyDrawTickets.map((t) => ({
              drawId: t.drawId,
              drawTitle: t.drawTitle,
              ticketPrice: t.ticketPrice,
              quantity: t.quantity,
            }))
          : undefined,
        serviceSchedule: (hasGarageServiceDeal || hasAnyDealItem)
          ? {
            date: String(serviceSchedule.date || '').trim(),
            timeSlot: String(serviceSchedule.timeSlot || '').trim(),
            pincode: String(serviceSchedule.pincode || '').trim(),
            vehicleNumber: String(serviceSchedule.vehicleNumber || '').trim()
          }
          : undefined,
        clearanceNoReturnAccepted: hasClearanceDealItem
          ? clearanceNoReturnAccepted || dealReturnPolicyAccepted
          : undefined,
        garageServiceNoReturnAccepted: hasGarageServiceDeal
          ? garageServiceNoReturnAccepted || dealReturnPolicyAccepted
          : undefined,
        dealReturnPolicyAccepted: cartDealIds.length > 0 ? dealReturnPolicyAccepted : undefined,
        flashCascadeConsentAccepted:
          flashCascadeAccepted || flashCascadeItems.length === 0 ? true : undefined,
        usedPartMeta: buyNowItem?.usedPartMeta || undefined,
        bodyPartMeta: buyNowItem?.bodyPartMeta || undefined,
      };

      // Create order via API
      let response;
      try {
        response = await orderService.createOrder(orderData);
      } catch (orderErr) {
        const code = orderErr?.response?.data?.code || orderErr?.code;
        if (code === 'FLASH_CASCADE_CONSENT_REQUIRED') {
          setShowFlashCascadeModal(true);
          setPaymentProcessing(false);
          toast.info(orderErr?.response?.data?.message || orderErr?.message);
          return;
        }
        throw orderErr;
      }
      
      if (response.order || response.success) {
        let order = response.order || response.data?.order;
        const orderId = order?.orderId || order?._id || order?.id;

        if (
          (hasGarageServiceDeal || isUsedPartExpressBuy || isUsedPartCheckAvailability) &&
          orderId &&
          selectedPayment !== 'cod' &&
          String(order?.paymentStatus || '') !== 'Paid'
        ) {
          try {
            const confirmed = await orderService.confirmOrderPayment(orderId);
            order = confirmed?.order || confirmed || order;
          } catch (confirmErr) {
            toast.error(mapOrderErrorToMessage(confirmErr?.message || confirmErr?.response?.data?.message));
            setPaymentProcessing(false);
            return;
          }
        }
        const orderStatus = order?.status || orderData.status || 'Order Placed';
        const paymentStatus = order?.paymentStatus || orderData.paymentStatus || 'Pending';
        
        // Send notifications
        if (orderId) {
          await notifyOrderStatus(orderId, orderStatus, order);
          await notifyPayment(orderId, paymentStatus, grandTotal);
        }
        
        const orderForConfirmation = {
          ...order,
          ...(Array.isArray(luckyDrawTickets) && luckyDrawTickets.length > 0 && { luckyDrawTickets }),
        };
        createdOrderRef.current = orderForConfirmation;
        setCreatedOrder(orderForConfirmation);
        setShowSuccessModal(true);
        setShowPaymentModal(false);

        // Clear checkout data after 2 seconds
        setTimeout(() => {
          if (isBuyNow) {
            // Buy-now: only clear the buy-now item — do NOT touch the user's cart
            localStorage.removeItem('buyNowItem');
            localStorage.removeItem('pendingBuyNowProduct');
          } else {
            // Normal cart checkout: clear the cart
            clearCart();
            localStorage.removeItem('pendingCartProduct');
          }
          localStorage.removeItem('shippingAddress');
          localStorage.removeItem('luckyDrawTickets');
          localStorage.removeItem('selectedShippingMethods');
          localStorage.removeItem('serviceSchedule');
        }, 2000);

        // Navigate to order confirmation after 3 seconds
        confirmationNavTimeoutRef.current = setTimeout(() => {
          goToOrderConfirmation();
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(mapOrderErrorToMessage(error?.message));
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const formatCardNumber = useCallback((value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  }, []);

  const formatExpiryDate = useCallback((value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  }, []);

  const handleCardNumberChange = useCallback((e) => {
    const formatted = formatCardNumber(e.target.value);
    setPaymentData(prev => ({ ...prev, cardNumber: formatted }));
  }, [formatCardNumber]);

  const handleExpiryChange = useCallback((e) => {
    const formatted = formatExpiryDate(e.target.value);
    setPaymentData(prev => ({ ...prev, expiryDate: formatted }));
  }, [formatExpiryDate]);

  const validatePaymentData = useCallback(() => {
    switch (selectedPayment) {
      case 'upi':
        if (!paymentData.upiId || !paymentData.upiId.includes('@')) {
          alert('Please enter a valid UPI ID (e.g., yourname@paytm)');
          return false;
        }
        break;
      case 'card':
        if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 16) {
          alert('Please enter a valid 16-digit card number');
          return false;
        }
        if (!paymentData.cardName) {
          alert('Please enter name on card');
          return false;
        }
        if (!paymentData.expiryDate || paymentData.expiryDate.length < 5) {
          alert('Please enter valid expiry date (MM/YY)');
          return false;
        }
        if (!paymentData.cvv || paymentData.cvv.length < 3) {
          alert('Please enter valid CVV');
          return false;
        }
        break;
      case 'netbanking':
        if (!paymentData.bankName) {
          alert('Please select a bank');
          return false;
        }
        break;
      case 'paytm':
        if (!paymentData.paytmPhone || paymentData.paytmPhone.length < 10) {
          alert('Please enter a valid Paytm phone number');
          return false;
        }
        break;
      case 'cod':
        return true;
      case 'razorpay':
        return true;
      case 'dummy':
        return true;
      default:
        return false;
    }
    return true;
  }, [selectedPayment, paymentData]);

  const banks = useMemo(() => [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Kotak Mahindra Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India',
    'Indian Bank',
  ], []);

  // Render payment form based on selected method
  const renderPaymentForm = useCallback(() => {
    if (!selectedPayment) return null;

    switch (selectedPayment) {
      case 'upi':
        return (
          <div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                UPI ID <span className="text-gray-700">*</span>
              </label>
              <input
                type="text"
                name="upiId"
                value={paymentData.upiId}
                onChange={handleInputChange}
                placeholder="yourname@paytm or yourname@ybl"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter your UPI ID (e.g., yourname@paytm, yourname@ybl, yourname@okaxis)
              </p>
            </div>
          </div>
        );

      case 'card':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Card Number <span className="text-gray-700">*</span>
              </label>
              <input
                type="text"
                name="cardNumber"
                value={paymentData.cardNumber}
                onChange={handleCardNumberChange}
                placeholder="1234 5678 9012 3456"
                maxLength="19"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Name on Card <span className="text-gray-700">*</span>
              </label>
              <input
                type="text"
                name="cardName"
                value={paymentData.cardName}
                onChange={handleInputChange}
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Expiry Date <span className="text-gray-700">*</span>
                </label>
                <input
                  type="text"
                  name="expiryDate"
                  value={paymentData.expiryDate}
                  onChange={handleExpiryChange}
                  placeholder="MM/YY"
                  maxLength="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  CVV <span className="text-gray-700">*</span>
                </label>
                <input
                  type="password"
                  name="cvv"
                  value={paymentData.cvv}
                  onChange={handleInputChange}
                  placeholder="123"
                  maxLength="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <FaLock className="text-gray-400" />
              <span>Your payment information is secure and encrypted</span>
            </div>
          </div>
        );

      case 'netbanking':
        return (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Bank Name <span className="text-gray-700">*</span>
            </label>
            <select
              name="bankName"
              value={paymentData.bankName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
              required
            >
              <option value="">Select your bank</option>
              {banks.map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
          </div>
        );

      case 'paytm':
        return (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Paytm Phone Number <span className="text-gray-700">*</span>
            </label>
            <div className="flex gap-2">
              <div className="w-20">
                <input
                  type="text"
                  value="+91"
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-center font-medium"
                />
              </div>
              <div className="flex-1">
                <input
                  type="tel"
                  name="paytmPhone"
                  value={paymentData.paytmPhone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  required
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Enter the mobile number linked to your Paytm account
            </p>
          </div>
        );

      case 'razorpay':
        return (
          <div>
            <p className="text-xs text-gray-600 mb-4">
              You will be redirected to Razorpay's secure payment gateway to complete your transaction.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-700">
                <strong>Secure Payment:</strong> Your payment will be processed securely by Razorpay. 
                We do not store your payment information.
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> After clicking "Pay & Place Order", you will be redirected to Razorpay's payment page. 
                Complete the payment there to confirm your order.
              </p>
            </div>
          </div>
        );

      case 'cod':
        return (
          <div>
            <p className="text-xs text-gray-600 mb-4">
              You will pay the order amount (₹{grandTotal.toFixed(2)}) when you receive your order.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-xs text-yellow-800">
                <strong>Note:</strong> Please keep exact change ready for delivery.
              </p>
            </div>
          </div>
        );

      case 'dummy':
        return (
          <div>
            <p className="text-xs text-gray-600 mb-4">
              This is a test payment method. No real charge will be made.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-700">
                <strong>Demo only:</strong> Use this to complete checkout without a real payment. Your order will be created and marked as paid for testing.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  }, [selectedPayment, paymentData, handleInputChange, handleCardNumberChange, handleExpiryChange, banks, grandTotal]);

  // Progress Bar Component
  // ProgressBar removed - using WorkflowWrapper instead
  const _ProgressBar = React.memo(() => (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-2 md:mt-4 mb-2 bg-white border border-gray-200 p-2 sm:p-3 md:p-4"
    >
      <div className="flex items-center justify-center space-x-0.5 sm:space-x-1 md:space-x-2">
        <motion.button
          onClick={() => navigate('/cart')}
          className="flex flex-col items-center cursor-pointer group flex-shrink-0 min-w-[45px] sm:min-w-[50px] md:min-w-[55px]"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div 
            className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-gray-100 rounded-full flex items-center justify-center mb-0.5 sm:mb-1 group-hover:bg-gray-200 transition-colors"
          >
            <FaShoppingCart className="text-gray-700 text-xs sm:text-sm md:text-base" />
          </motion.div>
          <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Cart</span>
        </motion.button>
        <motion.div 
          className="h-0.5 flex-1 sm:flex-none sm:w-6 md:w-8 lg:w-10 xl:w-12 bg-gray-300 rounded-full"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
        <motion.button
          onClick={() => navigate('/checkout/address')}
          className="flex flex-col items-center cursor-pointer group flex-shrink-0 min-w-[45px] sm:min-w-[50px] md:min-w-[55px]"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div 
            className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-gray-100 rounded-full flex items-center justify-center mb-0.5 sm:mb-1 group-hover:bg-gray-200 transition-colors"
          >
            <FaMapMarkerAlt className="text-gray-700 text-xs sm:text-sm md:text-base" />
          </motion.div>
          <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Address</span>
        </motion.button>
        <motion.div 
          className="h-0.5 flex-1 sm:flex-none sm:w-6 md:w-8 lg:w-10 xl:w-12 bg-gray-300 rounded-full"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        />
        <motion.button
          onClick={() => navigate('/checkout/review')}
          className="flex flex-col items-center cursor-pointer group flex-shrink-0 min-w-[45px] sm:min-w-[50px] md:min-w-[55px]"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div 
            className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-gray-100 rounded-full flex items-center justify-center mb-0.5 sm:mb-1 group-hover:bg-gray-200 transition-colors"
          >
            <FaFileAlt className="text-gray-700 text-xs sm:text-sm md:text-base" />
          </motion.div>
          <span className="text-[10px] sm:text-xs text-gray-600 font-medium">Review</span>
        </motion.button>
        <motion.div 
          className="h-0.5 flex-1 sm:flex-none sm:w-6 md:w-8 lg:w-10 xl:w-12 bg-gray-900 rounded-full"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        />
        <motion.div 
          className="flex flex-col items-center flex-shrink-0 min-w-[45px] sm:min-w-[50px] md:min-w-[55px]"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.7, type: "spring" }}
        >
          <motion.div 
            className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 bg-gray-900 rounded-full flex items-center justify-center mb-0.5 sm:mb-1 relative overflow-hidden"
          >
            <FaCreditCard className="text-white text-xs sm:text-sm md:text-base relative z-10" />
          </motion.div>
          <span className="text-[10px] sm:text-xs text-gray-900 font-semibold">Pay</span>
        </motion.div>
      </div>
    </motion.div>
  ));

  if (!shippingAddress) {
    return null;
  }

  return (
    <WorkflowWrapper currentStep="pay">
      <div className="checkout-light-page min-h-screen bg-gray-50 py-4">
        <div className="max-w-6xl mx-auto px-4">

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-4"
        >
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-1">
            Payment Method
          </h1>
          <p className="text-gray-600 text-sm">Choose your preferred payment option</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Payment Options */}
          <div className="lg:col-span-2">
            {(hasGarageServiceDeal || hasAnyDealItem) && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white border border-gray-200 p-4 sm:p-6 mb-4"
              >
                <h3 className="text-base font-semibold text-gray-900 mb-3">Service Schedule (for service deals)</h3>
                <p className="text-xs text-amber-700 mb-3 font-medium">
                  Fill this when your order includes service-based deals. These fields are ignored for non-service deals.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={serviceSchedule.date}
                    onChange={(e) => setServiceSchedule((p) => ({ ...p, date: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Time slot (e.g., 10:00-11:00)"
                    value={serviceSchedule.timeSlot}
                    onChange={(e) => setServiceSchedule((p) => ({ ...p, timeSlot: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Service pincode"
                    value={serviceSchedule.pincode}
                    onChange={(e) => setServiceSchedule((p) => ({ ...p, pincode: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Vehicle number (if required)"
                    value={serviceSchedule.vehicleNumber}
                    onChange={(e) => setServiceSchedule((p) => ({ ...p, vehicleNumber: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                {hasGarageServiceDeal && (
                  <>
                    <p className="text-xs text-indigo-800 mt-3 font-medium">
                      Garage service requires advance payment to lock your appointment slot. Cash on delivery is not available.
                    </p>
                    {garageBookingHint ? (
                      <p className="text-xs text-red-700 mt-2 font-semibold">{garageBookingHint}</p>
                    ) : null}
                  </>
                )}
              </motion.div>
            )}
            <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="mb-1 text-lg font-semibold text-gray-900 sm:text-xl">
                    Select Payment Method
                  </h2>
                  <p className="text-xs text-gray-600 sm:text-sm">
                    {hasGarageServiceDeal
                      ? 'Pay now to confirm and lock your garage appointment'
                      : 'Choose a secure payment option'}
                  </p>
                </div>
                <div className="hidden items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 sm:flex">
                  <FaShieldAlt className="text-sm text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-800">Secure</span>
                </div>
              </div>

              <div
                className="relative z-20 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2"
                role="radiogroup"
                aria-label="Payment methods"
              >
                {visiblePaymentOptions.length === 0 ? (
                  <p className="col-span-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    No payment methods are available for this order. Try refreshing the page or use a different checkout path.
                  </p>
                ) : (
                  visiblePaymentOptions.map((option) => (
                    <PaymentOptionCard
                      key={option.id}
                      option={option}
                      groupName="sparepe-payment-method"
                      isSelected={selectedPayment === option.id}
                      onSelect={handlePaymentOptionClick}
                    />
                  ))
                )}
              </div>

              {selectedPayment && (
                <p className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800 sm:text-sm">
                  Selected:{' '}
                  <strong>{paymentOptions.find((p) => p.id === selectedPayment)?.name}</strong>
                  {paymentMethodsNeedingForm.has(selectedPayment)
                    ? ' — enter details in the popup, or tap Pay & Place Order below.'
                    : ' — tap Pay & Place Order below to continue.'}
                </p>
              )}

              {/* Security Badge */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4 border-t border-gray-200 pt-6 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <FaLock className="text-gray-500" />
                  <span>256-bit SSL Encryption</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaShieldAlt className="text-gray-500" />
                  <span>PCI DSS Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-500" />
                  <span>100% Secure</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary Sidebar */}
          <div className="lg:col-span-1">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white border border-gray-200 p-4 sm:p-6 sticky top-4"
            >
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                <FaShoppingCart className="text-gray-700" />
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Order Summary</h2>
              </div>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm text-gray-700">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
                    {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
                  </span>
                  <span className="font-medium">₹{getTotalPrice().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Delivery Charge</span>
                  <span className={totalDeliveryCharge === 0 ? 'text-green-600 font-semibold' : 'font-medium'}>
                    {totalDeliveryCharge === 0 ? 'FREE' : formatPrice(totalDeliveryCharge)}
                  </span>
                </div>
                {Object.keys(selectedShippingMethods).length > 0 && (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-600 space-y-1">
                      {packages.map((pkg) => {
                        const methodId = selectedShippingMethods[pkg.packageNumber] || 'standard';
                        const method = shippingOptions.find(opt => opt.id === methodId);
                        return method ? (
                          <div key={pkg.packageNumber} className="flex items-center justify-between">
                            <span className="flex items-center gap-1">
                              <FaTruck className="text-xs" />
                              Package {pkg.packageNumber}: {method.name}
                            </span>
                            <span className="text-gray-500">
                              <FaClock className="text-xs inline mr-1" />
                              {method.days} day{method.days > 1 ? 's' : ''}
                            </span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Platform Fee</span>
                  <span className="font-medium">{formatPrice(platformFee)}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-700">
                    <span>Coupon discount</span>
                    <span className="font-medium">- {formatPrice(couponDiscount)}</span>
                  </div>
                )}
                {luckyDrawTotal > 0 && (
                  <div className="flex justify-between text-sm text-gray-700">
                    <span className="flex items-center gap-1.5">
                      <FaGift className="text-emerald-500 text-xs" />
                      Lucky Draw tickets
                    </span>
                    <span className="font-medium text-gray-900">{formatPrice(luckyDrawTotal)}</span>
                  </div>
                )}
              </div>

              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="border-t border-gray-200 pt-4 mb-4"
              >
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-lg font-semibold text-gray-900">Grand Total</span>
                  <span className="text-xl sm:text-2xl font-bold text-gray-900">
                    {formatPrice(grandTotal)}
                  </span>
                </div>
              </motion.div>

              {/* Trust Indicators */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                  <FaShieldAlt className="text-gray-500" />
                  <span className="font-medium">Your payment is secure</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <FaCheckCircle className="text-gray-500" />
                  <span>Protected by industry standards</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {cartDealIds.length > 0 && (
          <div className="mt-4 space-y-3">
            {cartDealIds.map((id) => (
              <DealReturnPolicyDisclosure
                key={id}
                dealId={id}
                checkpoint="checkout"
                requireAcceptance
                onAcceptedChange={(ok) => {
                  if (allDealReturnPoliciesAccepted(cartDealIds, 'checkout')) {
                    setDealReturnPolicyAccepted(true);
                  } else {
                    setDealReturnPolicyAccepted(false);
                  }
                }}
              />
            ))}
          </div>
        )}

        {hasClearanceDealItem && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={clearanceNoReturnAccepted}
                onChange={(e) => setClearanceNoReturnAccepted(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-amber-900">
                <strong>Clearance — final sale:</strong> I understand these items cannot be returned or exchanged. This is required to complete checkout.
              </span>
            </label>
          </div>
        )}

        {hasGarageServiceDeal && (
          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={garageServiceNoReturnAccepted}
                onChange={(e) => setGarageServiceNoReturnAccepted(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-indigo-950">
                <strong>Garage service booking:</strong> I understand this is a service appointment (not a part). It cannot be returned once performed. Cancelling within 24 hours of the appointment forfeits my advance payment.
              </span>
            </label>
          </div>
        )}

        {/* Footer Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white border border-gray-200 p-4 sm:p-6"
        >
          <button
            onClick={() => navigate('/checkout/review')}
            className="flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium w-full sm:w-auto justify-center"
          >
            <FaArrowLeft />
            Back
          </button>
          {selectedPayment && (
            <button
              onClick={async () => {
                if (paymentMethodsNeedingForm.has(selectedPayment)) {
                  const valid = validatePaymentData();
                  if (!valid) return;
                }
                await handleCreateOrder();
              }}
              disabled={
                paymentProcessing ||
                (cartDealIds.length > 0 && !dealReturnPolicyAccepted) ||
                (hasClearanceDealItem && !clearanceNoReturnAccepted && !dealReturnPolicyAccepted) ||
                (hasGarageServiceDeal && !garageServiceNoReturnAccepted && !dealReturnPolicyAccepted) ||
                (hasGarageServiceDeal && !!garageBookingHint)
              }
              className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-6 sm:py-3"
            >
              {paymentProcessing ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FaCheck className="text-sm" />
                  Pay & Place Order
                </>
              )}
            </button>
          )}
        </motion.div>
      </div>

      {/* Payment Details Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedPayment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPaymentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="checkout-light-page max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white">
                <div className="flex items-center gap-3">
                  {selectedPayment && (() => {
                    const option = paymentOptions.find(p => p.id === selectedPayment);
                    const IconComponent = option?.icon;
                    return IconComponent ? (
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md"
                        style={{ background: `linear-gradient(135deg, ${option.color}, ${option.color}dd)` }}
                      >
                        <IconComponent className="text-white text-lg" />
                      </div>
                    ) : null;
                  })()}
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                      {paymentOptions.find(p => p.id === selectedPayment)?.name} Payment
                    </h2>
                    <p className="text-xs text-gray-600">Enter your payment details</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    if (selectedPayment !== 'cod' && selectedPayment !== 'razorpay' && selectedPayment !== 'dummy') {
                      setPaymentData({
                        upiId: '',
                        cardNumber: '',
                        cardName: '',
                        expiryDate: '',
                        cvv: '',
                        bankName: '',
                        paytmPhone: '',
                      });
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Modal Body - Payment Form */}
              <div className="p-6">
                {renderPaymentForm()}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    if (selectedPayment !== 'cod' && selectedPayment !== 'razorpay' && selectedPayment !== 'dummy') {
                      setPaymentData({
                        upiId: '',
                        cardNumber: '',
                        cardName: '',
                        expiryDate: '',
                        cvv: '',
                        bankName: '',
                        paytmPhone: '',
                      });
                    }
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (selectedPayment === 'cod' || selectedPayment === 'razorpay' || selectedPayment === 'dummy') {
                      setShowPaymentModal(false);
                      await handleCreateOrder();
                      return;
                    }
                    if (validatePaymentData()) {
                      setShowPaymentModal(false);
                    }
                  }}
                  disabled={paymentProcessing}
                  className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {paymentProcessing ? (
                    <>
                      <FaSpinner className="text-xs animate-spin" />
                      Processing...
                    </>
                  ) : selectedPayment === 'dummy' || selectedPayment === 'cod' || selectedPayment === 'razorpay' ? (
                    <>
                      <FaCheck className="text-xs" />
                      Pay & Place Order
                    </>
                  ) : (
                    <>
                      <FaCheck className="text-xs" />
                      Confirm Payment Details
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flash cascade consent — primary OOS, backup seller */}
      <AnimatePresence>
        {showFlashCascadeModal && flashCascadeItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">Backup seller fulfillment</h3>
              <p className="text-sm text-gray-600 mb-4">
                The primary seller does not have enough deal stock. Your order may be fulfilled by a
                backup seller who joined this flash sale.
              </p>
              <ul className="space-y-2 mb-5 text-sm text-gray-700">
                {flashCascadeItems.map((row) => (
                  <li key={row.dealId} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <span className="font-semibold">{row.dealTitle || row.productName}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Routed via backup seller (primary seller out of stock)
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowFlashCascadeModal(false);
                    setFlashCascadeAccepted(false);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFlashCascadeAccepted(true);
                    setShowFlashCascadeModal(false);
                    toast.success('Backup seller consent saved. You can place your order.');
                  }}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-500"
                >
                  Accept backup seller
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10002] p-4 pointer-events-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="order-success-title"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="checkout-light-page bg-white rounded-lg shadow-xl p-8 max-w-md w-full relative z-10 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                {/* Success Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", damping: 20, stiffness: 300 }}
                  className="flex justify-center mb-6"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <FaCheckCircle className="text-4xl text-green-500" />
                  </div>
                </motion.div>

                {/* Success Title */}
                <h2 id="order-success-title" className="text-2xl font-bold text-gray-900 mb-2">
                  {(createdOrder?.paymentStatus || 'Pending') === 'Paid' ? 'Payment Successful!' : 'Order Placed!'}
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  {(createdOrder?.paymentStatus || 'Pending') === 'Paid'
                    ? 'Your payment is successful and your order is confirmed.'
                    : 'Your order has been placed and is awaiting payment confirmation.'}
                </p>

                {/* Order Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Order Total</span>
                    <span className="font-bold text-gray-900">₹{grandTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Payment Method</span>
                    <span className="font-medium text-gray-900">
                      {paymentOptions.find(p => p.id === selectedPayment)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-sm">Items</span>
                    <span className="font-medium text-gray-900">{getTotalItems()} item(s)</span>
                  </div>
                </div>

                {/* Redirect Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                  <p className="text-xs text-blue-800">
                    Redirecting to order confirmation in <span className="font-bold">3</span> seconds...
                  </p>
                </div>

                {/* Action Button */}
                <button
                  type="button"
                  onClick={goToOrderConfirmation}
                  className="w-full px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold cursor-pointer pointer-events-auto relative z-20"
                >
                  View Order Confirmation
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>
    </WorkflowWrapper>
  );
};

export default Payment;
        